import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { sheetsService, type HistoryGroup } from '@/services/sheets/sheetsService';
import { ReferidoPhaseFlow } from '@/components/referidos';
import type { Referido, ReferidoFormData } from '@/types';

type Badge = { label: string; cls: string };

const upper = (s: string) => (s || '').trim().toUpperCase();

// Columna S (Cita): estado de la cita. Lee sin importar mayúsculas/minúsculas.
function citaBadge(raw: string): Badge | null {
  const v = upper(raw);
  if (!v) return null;
  switch (v) {
    case 'AGENDO':
      return { label: 'Agendó', cls: 'bg-green-100 text-green-700' };
    case 'PENDIENTE':
      return { label: 'Pendiente', cls: 'bg-gray-100 text-gray-600' };
    case 'NO CONTESTA':
      return { label: 'No contesta', cls: 'bg-amber-100 text-amber-700' };
    case 'NO AGENDO':
      return { label: 'No agendó', cls: 'bg-slate-100 text-slate-600' };
    case 'CANCELO':
      return { label: 'Canceló', cls: 'bg-red-100 text-red-700' };
    case 'NO LLAMAR':
      return { label: 'No llamar', cls: 'bg-gray-700 text-white' };
    default:
      return { label: raw.trim(), cls: 'bg-gray-100 text-gray-600' };
  }
}

// Columna T (Resultado / Venta).
function resultadoBadge(raw: string): Badge | null {
  const v = upper(raw);
  if (!v) return null;
  switch (v) {
    case 'VENTA':
      return { label: 'Venta', cls: 'bg-green-600 text-white' };
    case 'INTERESADA':
      return { label: 'Interesada', cls: 'bg-amber-100 text-amber-700' };
    case 'NO VENTA':
      return { label: 'No venta', cls: 'bg-gray-100 text-gray-600' };
    case 'NO HOME':
      return { label: 'No home', cls: 'bg-slate-100 text-slate-600' };
    default:
      return { label: raw.trim(), cls: 'bg-gray-100 text-gray-600' };
  }
}

// Columna U (Monto): si es solo número le pone "$"; si ya trae símbolo/letras lo deja igual.
function formatMonto(raw: string): string | null {
  const v = (raw || '').trim();
  if (!v) return null;
  if (/[^0-9.,]/.test(v)) return v;
  return `$${v}`;
}

// Solo los dígitos de un texto (para buscar teléfonos sin importar guiones/espacios).
const onlyDigits = (s: string) => (s || '').replace(/\D/g, '');

// Formatea un monto numérico (columna A / venta al cliente) como moneda.
const formatMoneyNum = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

// ============================================================
// Modal: Agregar más referidos a un programa existente
// ------------------------------------------------------------
// Reusa el MISMO flujo de captura (ReferidoPhaseFlow) para que se vea idéntico.
// Al terminar, escribe filas nuevas que se agrupan con el programa original
// (mismo referidor + misma fecha) vía sheetsService.appendReferidosToProgram.
// ============================================================
interface AddReferidosModalProps {
  group: HistoryGroup;
  onClose: () => void;
  onSaved: () => void;
}

function AddReferidosModal({ group, onClose, onSaved }: AddReferidosModalProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [nuevos, setNuevos] = useState<ReferidoFormData[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleComplete = async () => {
    if (!user) return;
    if (nuevos.length === 0) {
      showToast('warning', 'Agrega al menos un referido');
      return;
    }

    setIsSaving(true);
    try {
      const now = new Date().toISOString();

      // Convertimos el formulario a objetos Referido (igual que en CapturePage),
      // para reusar la misma lógica del servicio.
      const referidos: Referido[] = nuevos.map((r) => ({
        id: uuidv4(),
        referidor_id: group.id, // referencia local; la agrupación real es por D+E+B en la hoja
        nombre: r.nombre,
        apellido: r.apellido,
        telefono: r.telefono,
        ciudad: r.ciudad,
        relacion: r.relacion,
        trabaja: r.trabaja,
        mensaje_enviado: r.mensaje_enviado || undefined,
        respuesta_referido: r.respuesta_referido || undefined,
        dato_extra: r.dato_extra || undefined,
        vendedor_id: user.email,
        synced: false,
        createdAt: now,
        updatedAt: now,
      }));

      await sheetsService.appendReferidosToProgram(group, user.email, user.name, referidos);

      showToast(
        'success',
        `${referidos.length} referido${referidos.length !== 1 ? 's' : ''} agregado${
          referidos.length !== 1 ? 's' : ''
        } a ${group.referidor_nombre}`
      );
      onSaved();
    } catch (err) {
      console.error('Error al agregar referidos:', err);
      showToast('error', 'No se pudieron agregar. Revisa tu conexión e intenta de nuevo.');
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex flex-col">
      <div className="bg-white w-full max-w-lg mx-auto mt-auto sm:my-auto rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Agregar referidos</h2>
            <p className="text-sm text-gray-500">a {group.referidor_nombre}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Flujo de captura (mismo que la captura normal) */}
        <div className="flex-1 overflow-y-auto p-4">
          <ReferidoPhaseFlow
            referidos={nuevos}
            setReferidos={setNuevos}
            onComplete={handleComplete}
          />
        </div>
      </div>

      {/* Overlay de guardado */}
      {isSaving && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-600">Guardando...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function HistoryPage() {
  const { user } = useAuth();
  const [captures, setCaptures] = useState<HistoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // Programa al que se le están agregando referidos (null = modal cerrado).
  const [addingTo, setAddingTo] = useState<HistoryGroup | null>(null);
  const { showToast } = useToast();
  // Edición del monto de venta al cliente (columna A) de un programa.
  const [montoEditId, setMontoEditId] = useState<string | null>(null);
  const [montoInput, setMontoInput] = useState('');
  const [savingMonto, setSavingMonto] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const groups = await sheetsService.getHistoryFromSheet(user.email, user.name);
      setCaptures(groups);
    } catch (err) {
      console.error('Error loading history:', err);
      setError('No se pudo cargar el historial. Revisa tu conexión e intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Guarda (o limpia) el monto de venta al cliente de un programa en la columna A.
  const saveMonto = useCallback(
    async (group: HistoryGroup) => {
      if (!user) return;
      const monto = montoInput ? parseFloat(montoInput) : 0;
      if (montoInput && (isNaN(monto) || monto < 0)) {
        showToast('warning', 'Ingresa un monto válido');
        return;
      }
      setSavingMonto(true);
      try {
        await sheetsService.setVentaMonto(group, user.email, user.name, monto);
        showToast(
          'success',
          monto > 0 ? `Venta de ${formatMoneyNum(monto)} guardada` : 'Monto de venta eliminado'
        );
        setMontoEditId(null);
        setMontoInput('');
        await loadHistory();
      } catch (err) {
        console.error('Error al guardar el monto:', err);
        showToast('error', 'No se pudo guardar el monto. Intenta de nuevo.');
      } finally {
        setSavingMonto(false);
      }
    },
    [user, montoInput, showToast, loadHistory]
  );

  // Filtro del buscador: nombre del referidor, nombre/apellido del referido,
  // o teléfono (de cualquiera de los dos). Insensible a mayúsculas y a guiones.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return captures;
    const qDigits = onlyDigits(q);

    return captures.filter((group) => {
      // Referidor por nombre
      if ((group.referidor_nombre || '').toLowerCase().includes(q)) return true;
      // Referidor por teléfono
      if (qDigits && onlyDigits(group.referidor_telefono).includes(qDigits)) return true;
      // Cualquier referido por nombre/apellido o teléfono
      return group.referidos.some((r) => {
        const full = `${r.nombre} ${r.apellido}`.toLowerCase();
        if (full.includes(q)) return true;
        if (qDigits && onlyDigits(r.telefono).includes(qDigits)) return true;
        return false;
      });
    });
  }, [captures, query]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  if (captures.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Sin capturas aún</h3>
        <p className="text-gray-500">Tus capturas de referidos aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Historial</h2>
        <span className="text-sm text-gray-500">
          {query.trim() ? `${filtered.length} de ${captures.length}` : `${captures.length} capturas`}
        </span>
      </div>

      {/* Buscador */}
      <div className="relative">
        <svg
          className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, apellido o teléfono…"
          className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Borrar búsqueda"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500">
            Sin resultados para "<span className="font-medium text-gray-700">{query.trim()}</span>".
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((group) => {
            // Resumen rapido del programa (visible sin desplegar)
            const ventasCount = group.referidos.filter((r) => upper(r.resultado) === 'VENTA').length;
            const agendadosCount = group.referidos.filter((r) => upper(r.cita) === 'AGENDO').length;
            const interesadasCount = group.referidos.filter((r) => upper(r.resultado) === 'INTERESADA').length;
            const hasResumen = ventasCount > 0 || agendadosCount > 0 || interesadasCount > 0 || group.venta_monto > 0;

            return (
              <div
                key={group.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedId(expandedId === group.id ? null : group.id)}
                  className="w-full p-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-success-100">
                      <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{group.referidor_nombre}</p>
                      <p className="text-sm text-gray-500">
                        {group.referidos.length} referido{group.referidos.length !== 1 ? 's' : ''} • {group.fecha}
                      </p>
                      {hasResumen && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {group.venta_monto > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">
                              {formatMoneyNum(group.venta_monto)}
                            </span>
                          )}
                          {ventasCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {ventasCount} venta{ventasCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {agendadosCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {agendadosCount} agendó
                            </span>
                          )}
                          {interesadasCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              {interesadasCount} interesada{interesadasCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform shrink-0 ${expandedId === group.id ? 'rotate-180' : ''
                      }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedId === group.id && (
                  <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-gray-600">{group.referidor_telefono}</span>
                      </div>
                      {group.regalo && (
                        <div className="flex items-center gap-2 text-sm">
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                          </svg>
                          <span className="text-gray-600">{group.regalo}</span>
                        </div>
                      )}
                    </div>

                    {/* Venta al cliente (monto, columna A) */}
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      {montoEditId === group.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-600 shrink-0">Venta $</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoFocus
                            value={montoInput}
                            onChange={(e) =>
                              setMontoInput(e.target.value.replace(/[^0-9.]/g, '').slice(0, 9))
                            }
                            placeholder="Ej: 1899"
                            className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => saveMonto(group)}
                            disabled={savingMonto}
                            className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 shrink-0"
                          >
                            {savingMonto ? '...' : 'Guardar'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMontoEditId(null);
                              setMontoInput('');
                            }}
                            className="px-2 py-1.5 text-sm text-gray-500 shrink-0"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : group.venta_monto > 0 ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm">
                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-600">
                              Venta al cliente:{' '}
                              <strong className="text-gray-900">{formatMoneyNum(group.venta_monto)}</strong>
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setMontoEditId(group.id);
                              setMontoInput(String(group.venta_monto));
                            }}
                            className="text-primary-600 text-xs font-medium"
                          >
                            Editar
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setMontoEditId(group.id);
                            setMontoInput('');
                          }}
                          className="w-full flex items-center justify-center gap-2 text-sm text-green-700 font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Registrar venta al cliente
                        </button>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">Referidos</p>
                      <div className="space-y-2">
                        {group.referidos.map((referido) => {
                          const cita = citaBadge(referido.cita);
                          const resultado = resultadoBadge(referido.resultado);
                          const monto = formatMonto(referido.monto);
                          const hasTmk = !!(cita || resultado || monto);

                          return (
                            <div
                              key={referido.id}
                              className="bg-white rounded-lg p-3 border border-gray-200"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {referido.nombre} {referido.apellido}
                                  </p>
                                  <p className="text-sm text-gray-500">{referido.telefono}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-gray-600">{referido.ciudad}</p>
                                  <p className="text-xs text-gray-400">{referido.relacion}</p>
                                </div>
                              </div>

                              {hasTmk && (
                                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                  {cita && (
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cita.cls}`}>
                                      {cita.label}
                                    </span>
                                  )}
                                  {resultado && (
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${resultado.cls}`}>
                                      {resultado.label}
                                    </span>
                                  )}
                                  {monto && (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                      {monto}
                                    </span>
                                  )}
                                </div>
                              )}

                              {referido.dato_extra && (
                                <p className="text-xs text-gray-500 mt-2 italic">
                                  "{referido.dato_extra}"
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Botón: agregar más referidos a ESTE programa */}
                    <button
                      type="button"
                      onClick={() => setAddingTo(group)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 active:bg-primary-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Agregar más referidos
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para agregar más referidos */}
      {addingTo && (
        <AddReferidosModal
          group={addingTo}
          onClose={() => setAddingTo(null)}
          onSaved={() => {
            setAddingTo(null);
            loadHistory();
          }}
        />
      )}
    </div>
  );
}
